import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Steps,
  Progress,
  Badge,
  Tooltip,
  Dropdown,
  Menu,
  Descriptions,
  Timeline,
  Alert,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  WarningOutlined,
  MoreOutlined,
  BankOutlined,
  CalendarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { VendorInvoice, Vendor, PurchaseOrder, InvoiceStatus, Payment } from '../../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;

interface VendorInvoicesProps {
  invoices: VendorInvoice[];
  vendors: Vendor[];
  purchaseOrders: PurchaseOrder[];
  onInvoiceCreate: (invoice: any) => void;
  onInvoiceUpdate: (id: string, data: any) => void;
  onInvoiceApprove: (id: string) => void;
}

export const VendorInvoices: React.FC<VendorInvoicesProps> = ({
  invoices,
  vendors,
  purchaseOrders,
  onInvoiceCreate,
  onInvoiceUpdate,
  onInvoiceApprove,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | undefined>();
  const [vendorFilter, setVendorFilter] = useState<string | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<VendorInvoice | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Mock data for demonstration
  const mockVendors: Vendor[] = [
    {
      id: '1',
      code: 'V001',
      name: 'ABC Supplies Inc.',
      type: 'supplier',
      category: 'office_supplies',
      status: 'active',
      contactInfo: {
        primaryContact: {
          name: 'John Smith',
          title: 'Sales Manager',
          email: 'john@abcsupplies.com',
          phone: '+1-555-0123',
        },
      },
      address: {
        billing: {
          street: '123 Business St',
          city: 'New York',
          state: 'NY',
          postalCode: '10001',
          country: 'USA',
        },
      },
      paymentTerms: {
        termCode: 'NET30',
        description: 'Net 30 days',
        daysNet: 30,
        currency: 'USD',
        paymentMethod: 'bank_transfer',
      },
      taxInfo: {
        taxId: '12-3456789',
        taxExempt: false,
        taxCategory: 'standard',
      },
      documents: [],
      rating: {
        overall: 4.5,
        quality: 4.0,
        delivery: 5.0,
        service: 4.5,
        price: 4.0,
        totalOrders: 25,
        onTimeDelivery: 95,
        lastEvaluated: new Date(),
      },
      certifications: [],
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'admin',
    },
  ];


  const [filteredData, setFilteredData] = useState(mockInvoices);

  useEffect(() => {
    let filtered = mockInvoices;

    if (searchText) {
      filtered = filtered.filter(invoice =>
        invoice.invoiceNumber.toLowerCase().includes(searchText.toLowerCase()) ||
        invoice.vendorInvoiceNumber.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }

    if (vendorFilter) {
      filtered = filtered.filter(invoice => invoice.vendorId === vendorFilter);
    }

    setFilteredData(filtered);
  }, [searchText, statusFilter, vendorFilter]);

  const getStatusColor = (status: InvoiceStatus): string => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending_payment': return 'processing';
      case 'partially_paid': return 'cyan';
      case 'paid': return 'green';
      case 'overdue': return 'red';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: InvoiceStatus) => {
    switch (status) {
      case 'approved': return <CheckCircleOutlined />;
      case 'pending_approval': return <ClockCircleOutlined />;
      case 'rejected': return <ExclamationCircleOutlined />;
      case 'paid': return <CheckCircleOutlined />;
      case 'overdue': return <WarningOutlined />;
      case 'pending_payment': return <CreditCardOutlined />;
      default: return <FileTextOutlined />;
    }
  };

  const getPaymentProgress = (invoice: VendorInvoice): number => {
    return (invoice.paidAmount / invoice.totalAmount) * 100;
  };

  const isOverdue = (invoice: VendorInvoice): boolean => {
    return new Date(invoice.dueDate) < new Date() && invoice.balanceAmount > 0;
  };

  const getDaysOverdue = (invoice: VendorInvoice): number => {
    const today = new Date();
    const dueDate = new Date(invoice.dueDate);
    const diffTime = today.getTime() - dueDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleAction = (record: VendorInvoice, action: string) => {
    switch (action) {
      case 'view':
        setSelectedInvoice(record);
        setIsModalVisible(true);
        break;
      case 'edit':
        onInvoiceUpdate(record.id, record);
        break;
      case 'approve':
        onInvoiceApprove(record.id);
        break;
      case 'reject':
        Modal.confirm({
          title: 'Reject Invoice',
          content: `Are you sure you want to reject invoice ${record.invoiceNumber}?`,
          onOk: () => onInvoiceUpdate(record.id, { ...record, status: 'rejected' }),
        });
        break;
      case 'pay':
        Modal.info({
          title: 'Process Payment',
          content: `Payment processing for invoice ${record.invoiceNumber} would be initiated here.`,
        });
        break;
    }
  };

  const actionMenu = (record: VendorInvoice) => (
    <Menu>
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleAction(record, 'view')}>
        View Details
      </Menu.Item>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleAction(record, 'edit')}>
        Edit
      </Menu.Item>
      {record.status === 'pending_approval' && (
        <>
          <Menu.Item key="approve" icon={<CheckCircleOutlined />} onClick={() => handleAction(record, 'approve')}>
            Approve
          </Menu.Item>
          <Menu.Item key="reject" icon={<ExclamationCircleOutlined />} onClick={() => handleAction(record, 'reject')}>
            Reject
          </Menu.Item>
        </>
      )}
      {(record.status === 'approved' || record.status === 'pending_payment' || record.status === 'partially_paid') && (
        <Menu.Item key="pay" icon={<CreditCardOutlined />} onClick={() => handleAction(record, 'pay')}>
          Process Payment
        </Menu.Item>
      )}
    </Menu>
  );

  const columns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 150,
      fixed: 'left' as const,
      render: (text: string, record: VendorInvoice) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleAction(record, 'view')}>
            {text}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.vendorInvoiceNumber}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorId',
      key: 'vendorId',
      width: 150,
      render: (vendorId: string) => {
        const vendor = mockVendors.find(v => v.id === vendorId);
        return vendor ? vendor.name : 'Unknown Vendor';
      },
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 120,
      render: (date: Date) => date.toLocaleDateString(),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (date: Date, record: VendorInvoice) => (
        <Space direction="vertical" size={0}>
          <Text style={{ color: isOverdue(record) ? '#ff4d4f' : '#000' }}>
            {date.toLocaleDateString()}
          </Text>
          {isOverdue(record) && (
            <Text type="danger" style={{ fontSize: '12px' }}>
              {getDaysOverdue(record)} days overdue
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: InvoiceStatus) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={
            <Space>
              {getStatusIcon(status)}
              <span style={{ textTransform: 'capitalize' }}>
                {status.replace('_', ' ')}
              </span>
            </Space>
          } 
        />
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount: number, record: VendorInvoice) => (
        <Text strong style={{ color: '#1890ff' }}>
          {record.currency} {amount.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      width: 160,
      render: (_, record: VendorInvoice) => {
        const progress = getPaymentProgress(record);
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space justify="space-between" style={{ width: '100%' }}>
              <Text style={{ fontSize: '12px' }}>
                Paid: {record.currency} {record.paidAmount.toLocaleString()}
              </Text>
              <Text style={{ fontSize: '12px' }}>
                {progress.toFixed(0)}%
              </Text>
            </Space>
            <Progress 
              percent={progress} 
              size="small" 
              showInfo={false}
              strokeColor={progress === 100 ? '#52c41a' : progress > 0 ? '#1890ff' : '#d9d9d9'}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Balance: {record.currency} {record.balanceAmount.toLocaleString()}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      fixed: 'right' as const,
      render: (_, record: VendorInvoice) => (
        <Dropdown overlay={actionMenu(record)} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  return (
    <div className="vendor-invoices">
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Invoices"
              value={filteredData.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Approval"
              value={filteredData.filter(i => i.status === 'pending_approval').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue"
              value={filteredData.filter(i => i.status === 'overdue').length}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Amount"
              value={filteredData.reduce((sum, i) => sum + i.totalAmount, 0)}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        title={<Title level={4}>Vendor Invoices</Title>}
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onInvoiceCreate({})}>
              New Invoice
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder="Search invoices..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="draft">Draft</Option>
              <Option value="pending_approval">Pending Approval</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="pending_payment">Pending Payment</Option>
              <Option value="partially_paid">Partially Paid</Option>
              <Option value="paid">Paid</Option>
              <Option value="overdue">Overdue</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Vendor"
              value={vendorFilter}
              onChange={setVendorFilter}
              allowClear
              style={{ width: '100%' }}
            >
              {mockVendors.map(vendor => (
                <Option key={vendor.id} value={vendor.id}>{vendor.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={10}>
            <RangePicker style={{ width: '100%' }} />
          </Col>
        </Row>

        {/* Overdue Alert */}
        {filteredData.some(i => i.status === 'overdue') && (
          <Alert
            message="Overdue Invoices"
            description={`You have ${filteredData.filter(i => i.status === 'overdue').length} overdue invoices requiring immediate attention.`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" type="primary">
                View Overdue
              </Button>
            }
          />
        )}

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: 8, background: '#f0f2f5', borderRadius: 4 }}>
            <Space>
              <Text>{selectedRowKeys.length} invoices selected</Text>
              <Button size="small">Bulk Approve</Button>
              <Button size="small">Process Payments</Button>
            </Space>
          </div>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1400 }}
          pagination={{
            total: filteredData.length,
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
          }}
        />
      </Card>

      {/* Invoice Details Modal */}
      <Modal
        title={`Invoice - ${selectedInvoice?.invoiceNumber}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          selectedInvoice?.status === 'pending_approval' && (
            <Button key="approve" type="primary" onClick={() => selectedInvoice && handleAction(selectedInvoice, 'approve')}>
              Approve Invoice
            </Button>
          ),
          (selectedInvoice?.status === 'approved' || selectedInvoice?.status === 'pending_payment') && (
            <Button key="pay" type="primary" onClick={() => selectedInvoice && handleAction(selectedInvoice, 'pay')}>
              Process Payment
            </Button>
          ),
        ]}
      >
        {selectedInvoice && (
          <div>
            <Descriptions column={2} bordered style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Invoice Number">{selectedInvoice.invoiceNumber}</Descriptions.Item>
              <Descriptions.Item label="Vendor Invoice">{selectedInvoice.vendorInvoiceNumber}</Descriptions.Item>
              <Descriptions.Item label="Vendor">{mockVendors.find(v => v.id === selectedInvoice.vendorId)?.name}</Descriptions.Item>
              <Descriptions.Item label="Purchase Order">
                {selectedInvoice.purchaseOrderId ? `PO-2024-${selectedInvoice.purchaseOrderId.padStart(3, '0')}` : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Invoice Date">{selectedInvoice.invoiceDate.toLocaleDateString()}</Descriptions.Item>
              <Descriptions.Item label="Due Date">
                <Space>
                  {selectedInvoice.dueDate.toLocaleDateString()}
                  {isOverdue(selectedInvoice) && <Tag color="red">Overdue</Tag>}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge status={getStatusColor(selectedInvoice.status) as any} text={selectedInvoice.status.replace('_', ' ')} />
              </Descriptions.Item>
              <Descriptions.Item label="Payment Terms">{selectedInvoice.paymentTerms.description}</Descriptions.Item>
            </Descriptions>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Card title="Invoice Summary" size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Subtotal:</Text>
                      <Text>{selectedInvoice.currency} {selectedInvoice.subtotal.toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Tax:</Text>
                      <Text>{selectedInvoice.currency} {selectedInvoice.taxAmount.toLocaleString()}</Text>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text strong>Total:</Text>
                      <Text strong>{selectedInvoice.currency} {selectedInvoice.totalAmount.toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Paid:</Text>
                      <Text style={{ color: '#52c41a' }}>{selectedInvoice.currency} {selectedInvoice.paidAmount.toLocaleString()}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text>Balance:</Text>
                      <Text style={{ color: selectedInvoice.balanceAmount > 0 ? '#ff4d4f' : '#52c41a' }}>
                        {selectedInvoice.currency} {selectedInvoice.balanceAmount.toLocaleString()}
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Payment Progress" size="small">
                  <Progress 
                    type="circle" 
                    percent={getPaymentProgress(selectedInvoice)} 
                    format={percent => `${percent?.toFixed(0)}%`}
                    strokeColor={getPaymentProgress(selectedInvoice) === 100 ? '#52c41a' : '#1890ff'}
                  />
                  <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Text type="secondary">
                      {getPaymentProgress(selectedInvoice) === 100 ? 'Fully Paid' : 
                       getPaymentProgress(selectedInvoice) > 0 ? 'Partially Paid' : 'Unpaid'}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>

            <Card title="Invoice Items" style={{ marginBottom: 24 }}>
              <Table
                dataSource={selectedInvoice.items}
                pagination={false}
                size="small"
                columns={[
                  { title: 'Product', dataIndex: 'productName', key: 'productName' },
                  { title: 'Description', dataIndex: 'description', key: 'description' },
                  { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
                  { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', render: (price: number) => `${selectedInvoice.currency} ${price}` },
                  { title: 'Total', dataIndex: 'totalPrice', key: 'totalPrice', render: (price: number) => `${selectedInvoice.currency} ${price}` },
                  { title: 'Tax', dataIndex: 'taxAmount', key: 'taxAmount', render: (tax: number) => `${selectedInvoice.currency} ${tax}` },
                  { title: 'Net Amount', dataIndex: 'netAmount', key: 'netAmount', render: (amount: number) => `${selectedInvoice.currency} ${amount}` },
                ]}
              />
            </Card>

            {selectedInvoice.payments.length > 0 && (
              <Card title="Payment History">
                <Timeline>
                  {selectedInvoice.payments.map((payment) => (
                    <Timeline.Item 
                      key={payment.id} 
                      color={payment.status === 'cleared' ? 'green' : payment.status === 'failed' ? 'red' : 'blue'}
                      dot={payment.status === 'cleared' ? <CheckCircleOutlined /> : <CreditCardOutlined />}
                    >
                      <Space direction="vertical" size={0}>
                        <Text strong>{payment.paymentNumber}</Text>
                        <Text>{selectedInvoice.currency} {payment.amount.toLocaleString()} - {payment.paymentMethod.replace('_', ' ')}</Text>
                        <Text type="secondary">{payment.paymentDate.toLocaleDateString()} - {payment.reference}</Text>
                        {payment.notes && <Text type="secondary" style={{ fontSize: '12px' }}>{payment.notes}</Text>}
                      </Space>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}; 